import { useState, useMemo, ReactNode, useEffect } from 'react';

import { Tab, Tabs as MUITabs, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';

import { useQueryParams } from 'hooks/common';

interface TabType {
  label: string;
  component: ReactNode;
}

interface TabsProps {
  tabs: Array<TabType>;
}

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => (
  <Typography
    component="div"
    role="tabpanel"
    hidden={value !== index}
    id={`tabpanel-${index}`}
    aria-labelledby={`tab-${index}`}
    {...other}
  >
    {children}
  </Typography>
);

const Tabs = ({ tabs }: TabsProps) => {
  const filteredTabs = tabs.filter(Boolean);
  const navigate = useNavigate();
  const [tabIndex, setTabIndex] = useState(0);
  const { search, pathname } = useLocation();
  const queryParams = useQueryParams();
  const queryTab = useMemo<number>(
    () => filteredTabs.map(tab => tab.label).indexOf(queryParams.get('tab')),
    [filteredTabs, queryParams],
  );

  useEffect(() => {
    if (queryTab !== tabIndex && queryTab !== -1) {
      setTabIndex(queryTab);
    } else if (queryTab !== tabIndex && queryTab === -1) {
      queryParams.set('tab', filteredTabs[0].label);
      navigate({ pathname, search: `?${queryParams.toString()}` }, { replace: true });
    }
  }, [queryTab, tabIndex, setTabIndex, queryParams, navigate, filteredTabs, pathname]);
  //left on purpose, since we discussed we might want to remove the filters when switching tabs. cool code nonetheless
  // const removeFiltersAndSorts = queryParams => {
  //   const keysToRemove = [];
  //   for (const key of queryParams.keys()) {
  //     if (key.startsWith('filter') || key.startsWith('sortBy')) {
  //       keysToRemove.push(key);
  //     }
  //   }
  //   keysToRemove.forEach(key => queryParams.delete(key));
  // };

  const handleChange = (index: number) => {
    const queryParams = new URLSearchParams(search);
    // removeFiltersAndSorts(queryParams);
    queryParams.set('tab', filteredTabs[index].label);
    navigate({ pathname, search: `?${queryParams.toString()}` });
  };

  return (
    <>
      <MUITabs
        value={tabIndex}
        indicatorColor="primary"
        textColor="primary"
        onChange={(_, index) => handleChange(index)}
        aria-label="tabs component"
        sx={{ borderBottom: '1px solid #e8e8e8', mb: 2 }}
      >
        {filteredTabs.map(({ label }, index) => (
          <Tab
            label={label}
            key={index}
            data-testid={`${label}-tab`}
            sx={{ textTransform: 'none', fontSize: 14, minWidth: { sm: '160px' } }}
          />
        ))}
      </MUITabs>
      {filteredTabs.map(({ component }, index: number) => (
        <TabPanel value={tabIndex} index={index} key={index}>
          {component}
        </TabPanel>
      ))}
    </>
  );
};

export default Tabs;
